import { Box, Button, Stack } from '@mui/material'
import DropDownOA from '../../common/dropDownOA'
import { Item } from '../../common/snippets'
import useToken, { originalUserSearchParams } from '../../helpers/useTokens'
import { GetRequest, PostRequestCachedV3 } from '../../common/api'
import { API_CACHE_DURATION, urls } from '../../common/constants'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { useEffect, useState } from 'react'
import { openIsolatedTab } from '../../common/helper'
import { useTranslation } from '../../../i18n/I18nProvider'

export const SelectUser = () => {
  const { translations } = useTranslation()
  const au = translations?.adminUpdate || {}
  const { loginData } = useToken()
  const { intCompanyID, intBranchID, strCompanyName, intUserID } = loginData
  const { IsBranchAdmin, IsMTPLAdmin, IsAdmin } = loginData
  const adminFlag = IsBranchAdmin || IsMTPLAdmin || IsAdmin
  const setLoader = useSetAtom(globalLoaderAtom)
  const setAlert = useSetAtom(alertAtom)
  const [companyList, setCompanyList] = useState([])
  const [selectedCompany, setSelectedCompany] = useState([])
  const [branchList, setBranchList] = useState([])
  const [selectedBranch, setSelectedBranch] = useState()
  const [userList, setUserList] = useState([])
  const [selectedUser, setSelectedUser] = useState()
  const getBranchID = (intCompanyID) => {
    setLoader(true)

    const payload = {
      intUserID: 0,
      intCompanyID,
      intBranchID: 0,
      strBranchName: '',
      strBranchCode: '',
      intStateID: 0,
      intCityID: 0,
      intIsActive: 1,
    }

    PostRequestCachedV3(urls.getBranchConfigOrEwbillinfo, payload, true, API_CACHE_DURATION)
      .then((res) => {
        if (res.status !== 'Success' || !Array.isArray(res?.Data?.Table)) {
          throw new Error(res?.ErrMessage || au.invalidResponse || 'Invalid Response')
        }
        if (!res.Data.Table.length) {
          throw new Error(au.noDataFound || 'No Data Found')
        }
        const list = res.Data.Table
        setBranchList(list)
        const defaultBranch = list.find((a) => a.BranchID === intBranchID) || list[0]
        setSelectedBranch(defaultBranch)
      })
      .catch((err) => {
        setBranchList([])
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }
  const getUserList = (intCompanyID, intBranchID) => {
    setLoader(true)
    const data = {
      intUserID: 0,
      intCompanyID,
      intBranchID,
      strUserName: '',
      strLoginID: '',
      strPassword: '',
      strContactNo: '',
      intRoleID: 0,
      intIsActive: 1,
    }
    PostRequestCachedV3(urls.getUserList, data, true, API_CACHE_DURATION)
      .then((res) => {
        if (res.status !== 'Success' || !Array.isArray(res?.Data?.Table)) {
          throw new Error(res?.ErrMessage || au.invalidResponse || 'Invalid Response')
        }

        if (!res.Data.Table.length) {
          throw new Error(au.noDataFound || 'No Data Found')
        }
        const list = res.Data.Table
        setUserList(list)
        const defaultUser = list.find((a) => a.UserID === intUserID) || list[0]
        setSelectedUser(defaultUser)
      })
      .catch((err) => {
        setUserList([])
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  useEffect(() => {
    if (intCompanyID) {
      getBranchID(intCompanyID)
      const loginCompany = { name: strCompanyName, id: intCompanyID }
      setSelectedCompany(loginCompany)
      setCompanyList([loginCompany])
    }
  }, [intCompanyID])

  useEffect(() => {
    if (selectedBranch?.BranchID) {
      getUserList(intCompanyID, selectedBranch.BranchID)
    }
  }, [selectedBranch])

  const handleLogin = () => {
    if (!adminFlag && selectedUser.RoleName.includes('Admin')) {
      setAlert({
        show: true,
        msg: au.nonAdminCannotSelectAdminUsers || 'Non-Admin user can not select Admin Users.',
        nextId: '',
      })
      return
    }
    const data = {
      CompanyID: selectedCompany?.id,
      UserID: selectedUser?.UserID,
    }
    GetRequest(urls.getAuthenticationToken, data)
      .then((res) => {
        if (res.status !== 'Success' || !res?.ReportURL) {
          throw new Error(res?.ErrMessage || au.invalidResponse || 'Invalid Response')
        }
        const url = `${window.location.origin}/?authentication=${res.AuthenticationToken}&${originalUserSearchParams(loginData)}`
        openIsolatedTab(url)
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  return (
    <div style={{ position: 'absolute', width: '100%' }}>
      <Item sx={{ padding: '0' }} className="reports">
        <Stack sx={{ minHeight: 100, minWidth: 650, marginTop: '8px' }}>
          <Box
            key={`for_otp_lnIp_`}
            sx={{
              width: '99%',
              marginLeft: '1%',
              display: 'inline-flex',
              padding: '0% 0px',
            }}
          >
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={companyList}
                defaultvalue={selectedCompany?.name || ''}
                width="100%"
                label={au.selectCompany || 'Select Company'}
                dataKey={`name`}
                idKey={`id`}
                onSelect={setSelectedCompany}
                disabled={true}
              />
            </Item>
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={branchList}
                defaultvalue={selectedBranch?.BranchName || ''}
                width="100%"
                label={au.selectBranch || 'Select Branch'}
                dataKey={`BranchName`}
                idKey={`BranchID`}
                onSelect={setSelectedBranch}
              />
            </Item>
            <Item sx={{ minWidth: '20%' }}>
              <DropDownOA
                data={userList}
                defaultvalue={selectedUser?.UserName || ''}
                width="100%"
                label={au.selectUser || 'Select User'}
                dataKey={`UserName`}
                idKey={`UserID`}
                onSelect={setSelectedUser}
              />
            </Item>
            <div style={{ display: 'flex', gap: '8px', margin: '10px 0px', justifyContent: 'end' }}>
              <Button
                className="select-lr-btn"
                variant="contained"
                color="secondary"
                sx={{ height: '100%', fontSize: '14px' }}
                onClick={handleLogin}
              >
                {au.login || 'Login'}
              </Button>
            </div>
          </Box>
        </Stack>
      </Item>
    </div>
  )
}
